import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatCalendar } from '@angular/material/datepicker';
import { finalize, Subject, takeUntil } from 'rxjs';
import { helper } from 'src/environments/helper';
import { NotificationService } from 'src/app/services/notification.service';
import { AddProgramareService } from './add-programare.service';
import Swal from 'sweetalert2';
import { countriesList, ICountryOption } from 'src/app/shared/countries-list';
import { PhoneNumberUtil } from 'google-libphonenumber'
const phoneNumberUtil = PhoneNumberUtil.getInstance();

interface SelectOption {
    id: number;
    name: string;
    web_name?: string;
}

interface PhoneData {
    dialCode: string;
    internationalNumber: string;
    isoCode: string;
    nationalNumber: string;
}

interface AvailableSlot {
    time: string;  // Format "HH:mm"
    label: string;
}

@Component({
    selector: 'app-add-programare',
    templateUrl: './add-programare.component.html',
    styleUrls: ['./add-programare.component.scss']
})
export class AddProgramareComponent implements OnInit, OnDestroy {
    @ViewChild('calendar') calendar!: MatCalendar<Date>;

    helper = helper;
    isLoading = false;
    isSaving = false;

    // Destroy subject for cleanup
    private destroy$ = new Subject<void>();

    // Temporary appointment ID for slot reservation (single-slot)
    private appointmentId: number | null = null;

    // Multi-slot support
    maxSloturi: number = 1;
    selectedSlots: { time: string, appointmentId: number }[] = [];

    // Select options
    module: SelectOption[] = [];
    locatii: SelectOption[] = [];
    servicii: SelectOption[] = [];
    tipuriProblema: SelectOption[] = [];
    tipuriProgram: SelectOption[] = [];
    availableSlots: AvailableSlot[] = [];

    // Calendar data
    minDate: Date = new Date();
    maxDate: Date = new Date(new Date().getFullYear() + 1, 11, 31);
    fullDays: string[] = [];
    zileLibere: string[] = [];
    selectedDate: Date | null = null;

    // phone
    roCountry: ICountryOption = countriesList.find(c => c.dial_code === "+40") || countriesList[0]
    selectedCountryCode: ICountryOption = this.roCountry;
    countryCodes: ICountryOption[] = countriesList;

    // Form
    form: FormGroup = new FormGroup({
        // Cascading selects
        id_modul: new FormControl('', [Validators.required]),
        id_locatie: new FormControl('', [Validators.required]),
        id_serviciu: new FormControl('', [Validators.required]),
        id_problem_type: new FormControl('', [Validators.required]),
        id_program_type: new FormControl('', [Validators.required]),

        // Date and time
        date: new FormControl('', [Validators.required]),
        time: new FormControl('', [Validators.required]),

        // Citizen data
        name: new FormControl('', [Validators.required, Validators.minLength(2)]),
        cnp: new FormControl('', [Validators.pattern('[0-9]{13}')]),
        phone: new FormControl('', [Validators.required, Validators.pattern('[0-9]{9,10}')]),
        email: new FormControl('', [Validators.email]),
        observatii: new FormControl('', [Validators.required, Validators.maxLength(250)]),

        // Notifications
        send_sms: new FormControl(true),
        send_email: new FormControl(true),
    });

    constructor(
        private dialogRef: MatDialogRef<AddProgramareComponent>,
        private notificationService: NotificationService,
        private addProgramareService: AddProgramareService,
    ) { }

    ngOnInit(): void {
        this.loadModule();
    }

    ngOnDestroy(): void {
        // Release all reserved slots
        this.releaseAllSlots();
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog(): void {
        this.dialogRef.close({ success: false });
    }

    // ========== LOAD DATA METHODS ==========

    loadModule(): void {
        this.isLoading = true;
        this.addProgramareService.getModuleList()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    this.module = res.data || [];
                    // Auto-select if single option
                    if (this.module.length === 1) {
                        this.form.patchValue({ id_modul: this.module[0].id });
                        this.onModulChange(this.module[0].id);
                    }
                },
                error: (err) => {
                    console.error('Error loading module:', err);
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        'Eroare la încărcarea modulelor',
                        helper.dialogConfig.icons.error
                    );
                }
            });
    }

    onModulChange(modulId: number): void {
        // Reset dependent fields
        this.form.patchValue({
            id_locatie: '',
            id_serviciu: '',
            id_problem_type: '',
            id_program_type: '',
            date: '',
            time: ''
        });
        this.locatii = [];
        this.servicii = [];
        this.tipuriProblema = [];
        this.tipuriProgram = [];
        this.availableSlots = [];
        this.selectedDate = null;
        this.releaseAllSlots();

        if (!modulId) return;

        this.isLoading = true;
        this.addProgramareService.getLocatiiByModul(modulId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    this.locatii = res.data || [];
                    // Auto-select if single option
                    if (this.locatii.length === 1) {
                        this.form.patchValue({ id_locatie: this.locatii[0].id });
                        this.onLocatieChange(this.locatii[0].id);
                    }
                },
                error: (err) => console.error('Error loading locatii:', err)
            });
    }

    onLocatieChange(locatieId: number): void {
        const modulId = this.form.value.id_modul;

        // Reset dependent fields
        this.form.patchValue({
            id_serviciu: '',
            id_problem_type: '',
            id_program_type: '',
            date: '',
            time: ''
        });
        this.servicii = [];
        this.tipuriProblema = [];
        this.tipuriProgram = [];
        this.availableSlots = [];
        this.selectedDate = null;
        this.releaseAllSlots();

        if (!locatieId || !modulId) return;

        this.isLoading = true;
        this.addProgramareService.getServiciiByModulLocatie(modulId, locatieId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    this.servicii = res.data || [];
                    // Auto-select if single option
                    if (this.servicii.length === 1) {
                        this.form.patchValue({ id_serviciu: this.servicii[0].id });
                        this.onServiciuChange(this.servicii[0].id);
                    }
                },
                error: (err) => console.error('Error loading servicii:', err)
            });
    }

    onServiciuChange(serviciuId: number): void {
        // Reset dependent fields
        this.form.patchValue({
            id_problem_type: '',
            id_program_type: '',
            date: '',
            time: ''
        });
        this.tipuriProblema = [];
        this.tipuriProgram = [];
        this.availableSlots = [];
        this.selectedDate = null;
        this.releaseAllSlots();

        if (!serviciuId) return;

        this.isLoading = true;

        // Load problem types
        this.addProgramareService.getTipuriProblemaByServiciu(serviciuId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.tipuriProblema = res.data || [];
                    // Auto-select if single option
                    if (this.tipuriProblema.length === 1) {
                        this.form.patchValue({ id_problem_type: this.tipuriProblema[0].id });
                    }
                },
                error: (err) => console.error('Error loading tipuri problema:', err)
            });

        // Load program types
        const { id_modul, id_locatie } = this.form.value;
        this.addProgramareService.getTipuriProgramByConfig(id_modul, id_locatie, serviciuId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    this.tipuriProgram = res.data || [];
                    // Auto-select if single option
                    if (this.tipuriProgram.length === 1) {
                        this.form.patchValue({ id_program_type: this.tipuriProgram[0].id });
                        this.onTipProgramChange(this.tipuriProgram[0].id);
                    }
                },
                error: (err) => console.error('Error loading tipuri program:', err)
            });
    }

    onTipProgramChange(tipProgramId: number): void {
        // Reset date and time
        this.form.patchValue({ date: '', time: '' });
        this.availableSlots = [];
        this.selectedDate = null;
        this.releaseAllSlots();

        if (!tipProgramId) return;

        this.loadCalendarData();
    }

    loadCalendarData(): void {
        const { id_modul, id_locatie, id_serviciu, id_program_type } = this.form.value;

        if (!id_modul || !id_locatie || !id_serviciu || !id_program_type) return;

        this.isLoading = true;

        // Load zile libere
        this.addProgramareService.getZileLibere()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.zileLibere = (res.data || []).map((z: any) => z.date);
                    // Forțează calendarul să re-evalueze dateFilter
                    if (this.calendar) {
                        this.calendar.updateTodaysDate();
                    }
                },
                error: (err) => console.error('Error loading zile libere:', err)
            });

        // Load full days
        this.addProgramareService.getFullDays(id_modul, id_locatie, id_serviciu, id_program_type)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    const data = res.data || {};
                    this.fullDays = data.fullDays || [];
                    if (data.minDate) {
                        this.minDate = new Date(data.minDate);
                    }
                    if (data.maxDate) {
                        this.maxDate = new Date(data.maxDate);
                    }
                    // Forțează calendarul să re-evalueze dateFilter
                    if (this.calendar) {
                        this.calendar.updateTodaysDate();
                    }
                },
                error: (err) => console.error('Error loading full days:', err)
            });
    }

    // ========== CALENDAR METHODS ==========

    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;

        const dateStr = this.formatDateToString(date);
        const dayOfWeek = date.getDay();

        // Exclude weekends
        if (dayOfWeek === 0 || dayOfWeek === 6) return false;

        // Exclude holidays
        if (this.zileLibere.includes(dateStr)) return false;

        // Exclude full days
        if (this.fullDays.includes(dateStr)) return false;

        return true;
    };

    onDateSelected(date: Date | null): void {
        if (!date) return;

        this.selectedDate = date;
        this.form.patchValue({
            date: this.formatDateToString(date),
            time: ''
        });
        this.availableSlots = [];
        this.releaseAllSlots();

        this.loadAvailableSlots();
    }

    loadAvailableSlots(): void {
        const { id_modul, id_locatie, id_serviciu, id_program_type, date } = this.form.value;

        if (!id_modul || !id_locatie || !id_serviciu || !id_program_type || !date) return;

        this.isLoading = true;
        this.addProgramareService.getAvailableSlots({
            id_modul,
            id_locatie,
            id_serviciu,
            id_program_type,
            date
        })
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isLoading = false)
            )
            .subscribe({
                next: (res: any) => {
                    const responseData = res?.data || res;
                    const times = responseData?.hours || responseData || [];
                    this.maxSloturi = responseData?.max_sloturi || 1;
                    this.availableSlots = (Array.isArray(times) ? times : []).map((time: string) => ({
                        time: time,
                        label: time
                    }));
                },
                error: (err) => console.error('Error loading available slots:', err)
            });
    }

    onSlotSelected(slot: AvailableSlot): void {
        if (this.maxSloturi > 1) {
            this.toggleMultiSlot(slot);
            return;
        }

        // Single-slot (legacy behavior)
        if (this.appointmentId) {
            this.releaseAllSlots();
        }
        this.form.patchValue({ time: slot.time });
        this.reserveSlot(slot.time);
    }

    toggleMultiSlot(slot: AvailableSlot): void {
        const existingIndex = this.selectedSlots.findIndex(s => s.time === slot.time);

        if (existingIndex >= 0) {
            // Deselect: release this slot
            const existing = this.selectedSlots[existingIndex];
            this.addProgramareService.resetAppointment({ appointmentIds: [existing.appointmentId] })
                .pipe(takeUntil(this.destroy$))
                .subscribe({ error: (err) => console.error('Error releasing slot:', err) });

            this.selectedSlots.splice(existingIndex, 1);
            this.form.patchValue({ time: this.selectedSlots.length > 0 ? this.selectedSlots[0].time : '' });
        } else if (this.selectedSlots.length < this.maxSloturi) {
            // Select: reserve this slot
            const { id_modul, id_locatie, id_serviciu, id_program_type, date } = this.form.value;

            this.addProgramareService.makeAppointment({
                id_modul, id_locatie, id_serviciu, id_program_type, date,
                time: slot.time
            })
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res: any) => {
                        const appointmentId = res.data || res || null;
                        if (appointmentId) {
                            this.selectedSlots.push({ time: slot.time, appointmentId });
                            this.form.patchValue({ time: this.selectedSlots[0].time });
                        }
                    },
                    error: (err) => console.error('Error reserving slot:', err)
                });
        }
    }

    isSlotSelected(slot: AvailableSlot): boolean {
        return this.selectedSlots.some(s => s.time === slot.time);
    }

    reserveSlot(slotTime: string): void {
        const { id_modul, id_locatie, id_serviciu, id_program_type, date } = this.form.value;

        this.addProgramareService.makeAppointment({
            id_modul,
            id_locatie,
            id_serviciu,
            id_program_type,
            date,
            time: slotTime
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.appointmentId = res.data || res || null;
                },
                error: (err) => {
                    console.error('Error reserving slot:', err);
                    this.form.patchValue({ time: '' });
                }
            });
    }

    releaseSlot(): void {
        if (!this.appointmentId) return;

        this.addProgramareService.resetAppointment({ appointmentIds: [this.appointmentId] })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.appointmentId = null;
                },
                error: (err) => console.error('Error releasing slot:', err)
            });
    }

    releaseAllSlots(): void {
        const allIds = this.selectedSlots.map(s => s.appointmentId);
        if (this.appointmentId) allIds.push(this.appointmentId);

        if (allIds.length > 0) {
            this.addProgramareService.resetAppointment({ appointmentIds: allIds })
                .pipe(takeUntil(this.destroy$))
                .subscribe({ error: (err) => console.error('Error releasing slots:', err) });
        }

        this.appointmentId = null;
        this.selectedSlots = [];
    }

    // ========== FORM ACTIONS ==========

    resetForm(): void {
        this.releaseAllSlots();

        this.form.reset({
            id_modul: '',
            id_locatie: '',
            id_serviciu: '',
            id_problem_type: '',
            id_program_type: '',
            date: '',
            time: '',
            name: '',
            cnp: '',
            phone: '',
            email: '',
            observatii: '',
            send_sms: true,
            send_email: true,
        });

        this.locatii = [];
        this.servicii = [];
        this.tipuriProblema = [];
        this.tipuriProgram = [];
        this.availableSlots = [];
        this.selectedDate = null;
        this.appointmentId = null;
        this.selectedSlots = [];
        this.maxSloturi = 1;
        this.selectedCountryCode = this.roCountry;

        this.loadModule();
    }

    submitForm(closeAfterSave: boolean = false): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.notificationService.warningSwal(
                helper.dialogConfig.headers.errorAtentie,
                'Vă rugăm să completați toate câmpurile obligatorii.',
                helper.dialogConfig.icons.error
            );
            return;
        }

        const allAppointmentIds = this.maxSloturi > 1
            ? this.selectedSlots.map(s => s.appointmentId)
            : (this.appointmentId ? [this.appointmentId] : []);

        if (allAppointmentIds.length === 0) {
            this.notificationService.warningSwal(
                helper.dialogConfig.headers.errorAtentie,
                'Vă rugăm să selectați un slot disponibil.',
                helper.dialogConfig.icons.error
            );
            return;
        }

        this.isSaving = true;

        const formValue = this.form.value;

        let phoneData;

        const telephoneNoInput = this.selectedCountryCode.dial_code + formValue.phone;
        console.log(telephoneNoInput);
        const phoneNo = phoneNumberUtil.parseAndKeepRawInput(telephoneNoInput);
        let regionCodeLower = '';
        let nationalNumberString = '';

        if (phoneNo) {
            const regionCode = phoneNumberUtil.getRegionCodeForNumber(phoneNo);
            if (regionCode) {
                regionCodeLower = (regionCode as string).toLowerCase();
            }

            const nationalNumber = phoneNo.getNationalNumber();
            if (nationalNumber) {
                nationalNumberString = nationalNumber.toString();
            }
        }

        phoneData = {
            dialCode: '',
            internationalNumber: telephoneNoInput,
            isoCode: regionCodeLower,
            nationalNumber: nationalNumberString,
        };

        phoneData.dialCode = telephoneNoInput.replace(phoneData.nationalNumber, '');
        phoneData.nationalNumber = phoneData.isoCode == 'ro' ? '0' + phoneData.nationalNumber : phoneData.nationalNumber;

        const payload = {
            id_modul: formValue.id_modul,
            id_locatie: formValue.id_locatie,
            id_serviciu: formValue.id_serviciu,
            id_problem_type: formValue.id_problem_type,
            id_program_type: formValue.id_program_type,
            appointmentIds: allAppointmentIds,
            data: {
                name: formValue.name,
                cnp: formValue.cnp,
                phone: formValue.phone,
                email: formValue.email,
                observatii: formValue.observatii,
            },
            phone: phoneData,
            send_sms: formValue.send_sms,
            send_email: formValue.send_email,
        };

        this.addProgramareService.storeProgramareOperator(payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.isSaving = false)
            )
            .subscribe({
                next: (res: any) => {
                    this.appointmentId = null;
                    this.selectedSlots = [];

                    // Show success with details
                    const data = res.data || {};
                    let notificationInfo = '';

                    if (data.sms_sent) {
                        notificationInfo += '<br><span class="text-success">✓ SMS trimis</span>';
                    }
                    if (data.email_sent) {
                        notificationInfo += '<br><span class="text-success">✓ Email trimis</span>';
                    }
                    if (data.email_mx_warning) {
                        notificationInfo += '<br><span class="text-warning">⚠ Email MX invalid</span>';
                    }

                    const slotsCount = data.slots_count || 1;
                    const appointments = data.appointments || [];
                    let slotsInfo = '';
                    if (appointments.length > 0) {
                        const list = appointments.map((a: any) =>
                            `<li>#${a.cerere_id} — ${a.date || 'N/A'}, ora ${a.time_from || 'N/A'}</li>`
                        ).join('');
                        slotsInfo = slotsCount > 1
                            ? `<p><strong>Nr. programări create:</strong> ${slotsCount}</p><ul class="text-start" style="margin: 4px 0;">${list}</ul>`
                            : `<p><strong>Programare:</strong></p><ul class="text-start" style="margin: 4px 0;">${list}</ul>`;
                    } else {
                        slotsInfo = `<p><strong>Număr programare:</strong> #${data.id || 'N/A'}</p>`;
                    }

                    Swal.fire({
                        title: helper.dialogConfig.headers.success,
                        html: `
                            <div class="text-start">
                                <p><strong>Programare creată cu succes!</strong></p>
                                <hr>
                                ${slotsInfo}
                                <p><strong>Data:</strong> ${data.reg_date || 'N/A'}</p>
                                <p><strong>Locație:</strong> ${data.locatie || 'N/A'}</p>
                                <p><strong>Serviciu:</strong> ${data.serviciu || 'N/A'}</p>
                                <p><strong>Cetățean:</strong> ${data.cetatean || 'N/A'}</p>
                                ${notificationInfo}
                            </div>
                        `,
                        icon: 'success',
                        confirmButtonText: helper.dialogConfig.buttonLabels.close,
                        allowOutsideClick: false,
                    }).then(() => {
                        if (closeAfterSave) {
                            this.dialogRef.close({ success: true });
                        } else {
                            // Keep config, reset citizen data and slot
                            this.resetForNewProgramare();
                        }
                    });
                },
                error: (err) => {
                    let msg = helper.dialogConfig.generalMessages.error;
                    try {
                        msg = err.error?.errors?.message?.[0] || err.error?.message || msg;
                    } catch (e) { }

                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        helper.dialogConfig.icons.error
                    );
                }
            });
    }

    resetForNewProgramare(): void {
        // Keep modul, locatie, serviciu, problem_type, program_type
        // Reset citizen data, date, time
        this.form.patchValue({
            date: '',
            time: '',
            name: '',
            cnp: '',
            phone: '',
            email: '',
            observatii: '',
        });

        this.availableSlots = [];
        this.selectedDate = null;
        this.appointmentId = null;
        this.selectedSlots = [];

        // Reload calendar data
        this.loadCalendarData();
    }

    // ========== UTILITY METHODS ==========

    formatDateToString(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    getSlotLabel(slot: AvailableSlot): string {
        return slot.label;
    }

    getSelectedSlot(): AvailableSlot | null {
        const time = this.form.value.time;
        return this.availableSlots.find(s => s.time === time) || null;
    }

    onCountryCodeChange(code: any): void {
        this.selectedCountryCode = code;
    }
}
